import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { normalizeProduct } from '@/utils/helper/product';

// Initial state
const initialState = {
  // All products
  products: [],
  productsLoading: false,
  productsError: null,
  productsLastFetched: null,
  
  // Single product
  currentProduct: null,
  currentProductLoading: false,
  currentProductError: null,
  currentProductVariations: [],
  currentProductReviews: [],
  
  // Best sellers
  bestSellers: [],
  bestSellersLoading: false,
  bestSellersError: null,
  
  // Admin products
  adminProducts: [],
  adminProductsLoading: false,
  adminProductsError: null,
  
  // Search and filters
  searchTerm: '',
  filters: {
    category: '',
    minPrice: null,
    maxPrice: null,
    inStock: null,
  },
  
  // Pagination
  pagination: {
    currentPage: 1,
    productsPerPage: 8,
    total: 0,
  },
};

// Async thunks
export const fetchAllProducts = createAsyncThunk(
  'products/fetchAllProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/products');
      // New API format: { success: true, products: [...], total: number }
      let products = [];
      if (response.data?.success && Array.isArray(response.data.products)) {
        products = response.data.products;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        products = response.data.data;
      } else if (response.data?.products && Array.isArray(response.data.products)) {
        products = response.data.products;
      }
      
      // Normalize all products
      const normalizedProducts = products.map(normalizeProduct);
      
      return {
        products: normalizedProducts,
        total: response.data?.total || normalizedProducts.length,
      };
    } catch (error) {
      console.error('Failed to fetch products:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/products/${productId}`);
      
      // New API format: { success: true, product: {...}, variations: [...], reviews: [...] }
      let productData = data;
      if (data?.success) {
        productData = data;
      }
      
      const normalizedProduct = normalizeProduct(productData.product || productData);
      
      return {
        product: normalizedProduct,
        variations: productData.variations || [],
        reviews: productData.reviews || [],
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product');
    }
  }
);

export const fetchBestSellers = createAsyncThunk(
  'products/fetchBestSellers',
  async (limit = 6, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/products/best-sellers?limit=${limit}`);
      
      let products = [];
      if (response.data?.success && Array.isArray(response.data.products)) {
        products = response.data.products;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        products = response.data.data;
      } else if (response.data?.products && Array.isArray(response.data.products)) {
        products = response.data.products;
      }
      
      const normalizedProducts = products.map(normalizeProduct);
      return normalizedProducts;
    } catch (error) {
      console.error('Failed to fetch best sellers:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch best sellers');
    }
  }
);

export const fetchAdminProducts = createAsyncThunk(
  'products/fetchAdminProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/products/all');
      
      let products = [];
      if (response.data?.products && Array.isArray(response.data.products)) {
        products = response.data.products;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      }
      
      return products;
    } catch (error) {
      console.error('Failed to fetch products for admin:', error);
      const message = error.response?.data?.error?.message || 'Failed to fetch products. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/products/upload', productData);
      const normalizedProduct = normalizeProduct(response.data.product || response.data);
      return normalizedProduct;
    } catch (error) {
      console.error('Error creating product:', error);
      const message = error.response?.data?.error?.message || error.message || 'Failed to create product. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ productId, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/products/${productId}`, data);
      const normalizedProduct = normalizeProduct(response.data.product || response.data);
      return normalizedProduct;
    } catch (error) {
      console.error('Error updating product:', error);
      const message = error.response?.data?.error?.message || error.message || 'Failed to update product. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (productId, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`/products/${productId}`);
      return productId;
    } catch (error) {
      console.error('Error deleting product:', error);
      const message = error.response?.data?.error?.message || error.message || 'Failed to delete product. Please try again.';
      return rejectWithValue(message);
    }
  }
);

export const fetchProductReviews = createAsyncThunk(
  'products/fetchProductReviews',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/products/${productId}/reviews`);
      return response.data.reviews || [];
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Failed to fetch product reviews.';
      return rejectWithValue(message);
    }
  }
);

export const submitProductReview = createAsyncThunk(
  'products/submitProductReview',
  async ({ productId, reviewData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/products/${productId}/reviews`, reviewData);
      return response.data.review;
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Failed to submit review. Please try again.';
      return rejectWithValue(message);
    }
  }
);

// Product slice
const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    // Search and filters
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.pagination.currentPage = 1; // Reset to first page on search
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page on filter
    },
    clearFilters: (state) => {
      state.searchTerm = '';
      state.filters = {
        category: '',
        minPrice: null,
        maxPrice: null,
        inStock: null,
      };
      state.pagination.currentPage = 1;
    },
    
    // Pagination
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setProductsPerPage: (state, action) => {
      state.pagination.productsPerPage = action.payload;
      state.pagination.currentPage = 1; // Reset to first page
    },
    
    // Clear current product
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
      state.currentProductVariations = [];
      state.currentProductReviews = [];
      state.currentProductError = null;
    },
    
    // Update product in list (for optimistic updates)
    updateProductInList: (state, action) => {
      const updatedProduct = action.payload;
      const index = state.products.findIndex(
        (p) => p.productId === updatedProduct.productId || p.id === updatedProduct.id
      );
      if (index !== -1) {
        state.products[index] = normalizeProduct(updatedProduct);
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch all products
    builder
      .addCase(fetchAllProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload.products;
        state.pagination.total = action.payload.total;
        state.productsLastFetched = Date.now();
        state.productsError = null;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      });

    // Fetch product by ID
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.currentProductLoading = true;
        state.currentProductError = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.currentProductLoading = false;
        state.currentProduct = action.payload.product;
        state.currentProductVariations = action.payload.variations;
        state.currentProductReviews = action.payload.reviews;
        state.currentProductError = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.currentProductLoading = false;
        state.currentProductError = action.payload;
      });

    // Fetch best sellers
    builder
      .addCase(fetchBestSellers.pending, (state) => {
        state.bestSellersLoading = true;
        state.bestSellersError = null;
      })
      .addCase(fetchBestSellers.fulfilled, (state, action) => {
        state.bestSellersLoading = false;
        state.bestSellers = action.payload;
        state.bestSellersError = null;
      })
      .addCase(fetchBestSellers.rejected, (state, action) => {
        state.bestSellersLoading = false;
        state.bestSellersError = action.payload;
      });

    // Fetch admin products
    builder
      .addCase(fetchAdminProducts.pending, (state) => {
        state.adminProductsLoading = true;
        state.adminProductsError = null;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.adminProductsLoading = false;
        state.adminProducts = action.payload;
        state.adminProductsError = null;
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.adminProductsLoading = false;
        state.adminProductsError = action.payload;
      });

    // Create product
    builder
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.unshift(action.payload);
        state.pagination.total += 1;
      });

    // Update product
    builder
      .addCase(updateProduct.fulfilled, (state, action) => {
        const updatedProduct = action.payload;
        // Update in products list
        const index = state.products.findIndex(
          (p) => p.productId === updatedProduct.productId || p.id === updatedProduct.id
        );
        if (index !== -1) {
          state.products[index] = updatedProduct;
        }
        // Update current product if it's the same
        if (state.currentProduct && 
            (state.currentProduct.productId === updatedProduct.productId || 
             state.currentProduct.id === updatedProduct.id)) {
          state.currentProduct = updatedProduct;
        }
        // Update in admin products
        const adminIndex = state.adminProducts.findIndex(
          (p) => (p.productId || p.id) === (updatedProduct.productId || updatedProduct.id)
        );
        if (adminIndex !== -1) {
          state.adminProducts[adminIndex] = { ...state.adminProducts[adminIndex], ...updatedProduct };
        }
      });

    // Delete product
    builder
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const productId = action.payload;
        state.products = state.products.filter(
          (p) => p.productId !== productId && p.id !== productId
        );
        state.adminProducts = state.adminProducts.filter(
          (p) => (p.productId || p.id) !== productId
        );
        if (state.currentProduct && 
            (state.currentProduct.productId === productId || state.currentProduct.id === productId)) {
          state.currentProduct = null;
        }
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      });

    // Fetch product reviews
    builder
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        state.currentProductReviews = action.payload;
      });

    // Submit product review
    builder
      .addCase(submitProductReview.fulfilled, (state, action) => {
        state.currentProductReviews.push(action.payload);
        // Update review count in current product
        if (state.currentProduct) {
          state.currentProduct.reviews = (state.currentProduct.reviews || 0) + 1;
        }
      });
  },
});

export const {
  setSearchTerm,
  setFilters,
  clearFilters,
  setCurrentPage,
  setProductsPerPage,
  clearCurrentProduct,
  updateProductInList,
} = productSlice.actions;

// Selectors
export const selectAllProducts = (state) => state.products.products;
export const selectProductsLoading = (state) => state.products.productsLoading;
export const selectProductsError = (state) => state.products.productsError;

export const selectCurrentProduct = (state) => state.products.currentProduct;
export const selectCurrentProductLoading = (state) => state.products.currentProductLoading;
export const selectCurrentProductError = (state) => state.products.currentProductError;
export const selectCurrentProductVariations = (state) => state.products.currentProductVariations;
export const selectCurrentProductReviews = (state) => state.products.currentProductReviews;

export const selectBestSellers = (state) => state.products.bestSellers;
export const selectBestSellersLoading = (state) => state.products.bestSellersLoading;
export const selectBestSellersError = (state) => state.products.bestSellersError;

export const selectAdminProducts = (state) => state.products.adminProducts;
export const selectAdminProductsLoading = (state) => state.products.adminProductsLoading;
export const selectAdminProductsError = (state) => state.products.adminProductsError;

export const selectSearchTerm = (state) => state.products.searchTerm;
export const selectFilters = (state) => state.products.filters;
export const selectPagination = (state) => state.products.pagination;

// Computed selectors
export const selectFilteredProducts = (state) => {
  const products = state.products.products;
  const searchTerm = state.products.searchTerm.toLowerCase();
  const filters = state.products.filters;

  return products.filter((product) => {
    // Search filter
    if (searchTerm) {
      const matchesSearch =
        product.name?.toLowerCase().includes(searchTerm) ||
        product.description?.toLowerCase().includes(searchTerm) ||
        product.category?.toLowerCase().includes(searchTerm);
      if (!matchesSearch) return false;
    }

    // Category filter (case-insensitive)
    if (filters.category) {
      const productCategory = (product.category || product.category_name || '').toLowerCase();
      const filterCategory = filters.category.toLowerCase();
      if (productCategory !== filterCategory) {
        return false;
      }
    }

    // Price filters
    if (filters.minPrice !== null && product.price < filters.minPrice) {
      return false;
    }
    if (filters.maxPrice !== null && product.price > filters.maxPrice) {
      return false;
    }

    // Stock filter
    if (filters.inStock !== null) {
      const isInStock = (product.stock_quantity || 0) > 0;
      if (filters.inStock !== isInStock) {
        return false;
      }
    }

    return true;
  });
};

export const selectPaginatedProducts = (state) => {
  const filtered = selectFilteredProducts(state);
  const { currentPage, productsPerPage } = state.products.pagination;
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  return filtered.slice(startIndex, endIndex);
};

export const selectTotalPages = (state) => {
  const filtered = selectFilteredProducts(state);
  const { productsPerPage } = state.products.pagination;
  return Math.ceil(filtered.length / productsPerPage);
};

export default productSlice.reducer;

